import React, { useEffect, useState, useCallback } from "react";
import { BillingAuditCard } from "@/components/BillingAuditCard";
import { TwilioCostSyncCard } from "@/components/TwilioCostSyncCard";
import { CarriersAdmin } from "@/components/CarriersAdmin";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  DollarSign, TrendingUp, PhoneCall, Users, AlertTriangle, Clock, Save, Ban, CheckCircle2, Loader2, Phone, RefreshCw, KeyRound, Star, Mail,
} from "lucide-react";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  ResponsiveContainer, ComposedChart, BarChart, Bar, Line, XAxis, YAxis,
  Tooltip, Tooltip as ChartTooltip, Legend, CartesianGrid,
} from "recharts";

const AXIS_TICK = { fontSize: 11 };
const BAR_RADIUS = [6, 6, 0, 0];

function renderToggleIcon(busy, status) {
  if (busy) return <Loader2 className="h-3.5 w-3.5 animate-spin" />;
  if (status === "active") return <Ban className="h-3.5 w-3.5" />;
  return <CheckCircle2 className="h-3.5 w-3.5" />;
}

export default function Admin() {
  return (
    <div>
      <h1 className="font-heading text-2xl font-bold tracking-tight mb-1">Admin dashboard</h1>
      <p className="text-zinc-500 text-sm mb-6">Rates, margin, usage and revenue control.</p>
      <Tabs defaultValue="overview">
        <div className="overflow-x-auto -mx-4 px-4 mb-6">
          <TabsList className="w-max">
          <TabsTrigger value="overview" data-testid="admin-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="deposits" data-testid="admin-tab-deposits">Deposits</TabsTrigger>
          <TabsTrigger value="rates" data-testid="admin-tab-rates">Rates & Margin</TabsTrigger>
          <TabsTrigger value="numbers" data-testid="admin-tab-numbers">Numbers</TabsTrigger>
          <TabsTrigger value="carriers" data-testid="admin-tab-carriers">Carriers &amp; Routes</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger>
          <TabsTrigger value="esims" data-testid="admin-tab-esims">eSIMs</TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="overview"><Overview /></TabsContent>
        <TabsContent value="deposits"><DepositsAdmin /></TabsContent>
        <TabsContent value="rates"><RatesAdmin /></TabsContent>
        <TabsContent value="numbers"><NumbersAdmin /></TabsContent>
        <TabsContent value="carriers"><CarriersAdmin /></TabsContent>
        <TabsContent value="users"><UsersAdmin /></TabsContent>
        <TabsContent value="esims"><EsimsAdmin /></TabsContent>
      </Tabs>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white border border-black/10 rounded-2xl p-5 shadow-sm">
      <div className={`h-9 w-9 rounded-xl flex items-center justify-center mb-3 ${accent || "bg-accent"}`}>
        <Icon className="h-4 w-4 text-primary" />
      </div>
      <div className="text-xs uppercase tracking-wide text-zinc-400 font-bold">{label}</div>
      <div className="font-mono font-bold text-2xl mt-0.5">{value}</div>
    </div>
  );
}

function DepositsAdmin() {
  const [data, setData] = useState(null);
  const [pnl, setPnl] = useState(null);

  useEffect(() => {
    api.get("/admin/deposits").then(({ data }) => setData(data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
    api.get("/admin/pnl").then(({ data }) => setPnl(data)).catch(() => {});
  }, []);

  if (!data) return <div className="p-8 text-center text-zinc-400">Loading deposits…</div>;

  const fmtDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) +
      " · " + d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  };
  const usd = (v) => `${Number(v || 0) < 0 ? "−" : ""}$${Math.abs(Number(v || 0)).toFixed(2)}`;

  return (
    <div data-testid="admin-deposits">
      {pnl && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
            <Stat icon={DollarSign} label="Money in (deposits)" value={usd(pnl.deposits_gross)} />
            <Stat icon={AlertTriangle} label="All expenses" value={`−${usd(pnl.expenses_total)}`} accent="bg-red-100" />
            <div className="bg-[#1E3A8A] text-white rounded-2xl p-5 shadow-sm" data-testid="pnl-net-card">
              <div className="h-9 w-9 rounded-xl bg-white/15 flex items-center justify-center mb-3">
                <TrendingUp className="h-4 w-4 text-white" />
              </div>
              <div className="text-xs uppercase tracking-wide text-blue-200 font-bold">You keep (net, cash)</div>
              <div className={`font-mono font-bold text-2xl mt-0.5 ${pnl.net_cash >= 0 ? "text-emerald-300" : "text-red-300"}`}>{usd(pnl.net_cash)}</div>
            </div>
          </div>

          <div className="bg-white border border-black/10 rounded-2xl shadow-sm overflow-hidden mb-6">
            <h3 className="font-heading font-bold p-5 pb-3">Where the money goes — per revenue stream</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="pnl-breakdown-table">
                <thead className="bg-zinc-50 border-y border-black/10 text-xs uppercase tracking-wide text-zinc-400 font-bold text-left">
                  <tr>
                    <th className="px-5 py-2.5">Stream</th>
                    <th className="px-5 py-2.5 text-right">Customers paid</th>
                    <th className="px-5 py-2.5 text-right">Provider cost</th>
                    <th className="px-5 py-2.5 text-right">You keep</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-black/5">
                    <td className="px-5 py-3 font-semibold">📞 Calls <span className="text-xs text-zinc-400 font-normal">({pnl.calls.count} completed)</span></td>
                    <td className="px-5 py-3 text-right font-mono">{usd(pnl.calls.revenue)}</td>
                    <td className="px-5 py-3 text-right font-mono text-red-500">−{usd(pnl.calls.twilio_cost)} <span className="text-[10px] text-zinc-400">Twilio</span></td>
                    <td className="px-5 py-3 text-right font-mono font-bold text-emerald-600">{usd(pnl.calls.profit)}</td>
                  </tr>
                  <tr className="border-b border-black/5">
                    <td className="px-5 py-3 font-semibold">📶 eSIMs <span className="text-xs text-zinc-400 font-normal">({pnl.esims.count} sold)</span></td>
                    <td className="px-5 py-3 text-right font-mono">{usd(pnl.esims.revenue)}</td>
                    <td className="px-5 py-3 text-right font-mono text-red-500">−{usd(pnl.esims.provider_cost)} <span className="text-[10px] text-zinc-400">eSIM Access</span></td>
                    <td className="px-5 py-3 text-right font-mono font-bold text-emerald-600">{usd(pnl.esims.profit)}</td>
                  </tr>
                  <tr className="border-b border-black/5">
                    <td className="px-5 py-3 font-semibold">💳 Card processing <span className="text-xs text-zinc-400 font-normal">(est. 2.9% + $0.30)</span></td>
                    <td className="px-5 py-3 text-right font-mono text-zinc-400">—</td>
                    <td className="px-5 py-3 text-right font-mono text-red-500">−{usd(pnl.stripe_fees_est)} <span className="text-[10px] text-zinc-400">Stripe</span></td>
                    <td className="px-5 py-3 text-right font-mono text-zinc-400">—</td>
                  </tr>
                  <tr className="bg-zinc-50">
                    <td className="px-5 py-3 font-bold">Earned profit after ALL expenses</td>
                    <td className="px-5 py-3" />
                    <td className="px-5 py-3" />
                    <td className={`px-5 py-3 text-right font-mono font-bold text-lg ${pnl.earned_margin >= 0 ? "text-emerald-600" : "text-red-500"}`} data-testid="pnl-earned-margin">{usd(pnl.earned_margin)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="px-5 pb-4 text-xs text-zinc-400">
              💡 Customers still hold <b>{usd(pnl.wallet_liability)}</b> of unspent wallet credit — cash you've received that becomes profit as they use it on calls & eSIMs.
            </p>
          </div>
          {pnl.monthly?.length > 0 && (
            <div className="bg-white border border-black/10 rounded-2xl shadow-sm p-5 mb-6" data-testid="pnl-monthly-chart">
              <h3 className="font-heading font-bold mb-1">Month-by-month growth</h3>
              <p className="text-xs text-zinc-400 mb-4">Blue bars: money deposited · Green line: profit after all expenses</p>
              <div style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer>
                  <ComposedChart data={pnl.monthly}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v}`} />
                    <ChartTooltip formatter={(v) => `$${Number(v).toFixed(2)}`} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="deposits" name="Deposits in" fill="#2563EB" radius={[6, 6, 0, 0]} maxBarSize={42} />
                    <Line dataKey="profit" name="Profit (after expenses)" stroke="#059669" strokeWidth={2.5} dot={{ r: 4 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <Stat icon={DollarSign} label="Total deposited" value={`$${data.total_deposited.toFixed(2)}`} />
        <Stat icon={TrendingUp} label="Deposited this month" value={`$${data.month_deposited.toFixed(2)}`} />
        <Stat icon={Users} label="Deposits" value={data.count} />
      </div>

      <div className="bg-white border border-black/10 rounded-2xl shadow-sm overflow-hidden">
        <h3 className="font-heading font-bold p-5 pb-3">All deposits</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-deposits-table">
            <thead className="bg-zinc-50 border-y border-black/10 text-xs uppercase tracking-wide text-zinc-400 font-bold text-left">
              <tr>
                <th className="px-5 py-2.5">Account</th>
                <th className="px-5 py-2.5">Phone</th>
                <th className="px-5 py-2.5 text-right">Deposited (USD)</th>
                <th className="px-5 py-2.5 text-right">Charged</th>
                <th className="px-5 py-2.5">Date & time</th>
                <th className="px-5 py-2.5">Stripe reference</th>
              </tr>
            </thead>
            <tbody>
              {data.deposits.length === 0 && (
                <tr><td colSpan={5} className="px-5 py-8 text-center text-zinc-400">No deposits yet.</td></tr>
              )}
              {data.deposits.map((d) => (
                <tr key={d.id} className="border-b border-black/5 last:border-0 hover:bg-zinc-50">
                  <td className="px-5 py-3">
                    <div className="font-semibold">{d.name || "—"}</div>
                    <div className="text-xs text-zinc-400">{d.email}</div>
                  </td>
                  <td className="px-5 py-3 font-mono text-xs text-zinc-500">{d.phone || "—"}</td>
                  <td className="px-5 py-3 text-right font-mono font-bold text-emerald-600">+${d.amount.toFixed(2)}</td>
                  <td className="px-5 py-3 text-right font-mono text-xs text-zinc-500">
                    {d.charged_amount ? `${Number(d.charged_amount).toFixed(2)} ${d.charged_currency}` : "—"}
                  </td>
                  <td className="px-5 py-3 text-zinc-500 whitespace-nowrap">{fmtDate(d.created_at)}</td>
                  <td className="px-5 py-3">
                    {d.stripe_ref ? (
                      d.stripe_ref.startsWith("pi_") ? (
                        <a
                          href={`https://dashboard.stripe.com/payments/${d.stripe_ref}`}
                          target="_blank" rel="noreferrer"
                          className="font-mono text-[11px] text-[#2563EB] hover:underline"
                          title="Open in Stripe dashboard"
                          data-testid={`stripe-ref-${d.id}`}
                        >
                          {d.stripe_ref.slice(0, 18)}…
                        </a>
                      ) : (
                        <button
                          onClick={() => { navigator.clipboard.writeText(d.stripe_ref); toast.success("Stripe reference copied"); }}
                          className="font-mono text-[11px] text-zinc-500 hover:text-zinc-900"
                          title={`${d.stripe_ref} — click to copy, then search it in Stripe → Payments`}
                          data-testid={`stripe-ref-${d.id}`}
                        >
                          {d.stripe_ref.slice(0, 18)}…
                        </button>
                      )
                    ) : (
                      <span className="text-zinc-300">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Overview() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data));
  }, []);

  if (!stats) return <div className="p-8 text-center text-zinc-400">Loading…</div>;

  return (
    <div data-testid="admin-overview">
      {!stats.twilio_enabled && (
        <div className="mb-6 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4" />
          <span>Calling runs in <b>simulated mode</b>. Add Twilio credentials in backend/.env to place real calls.</span>
        </div>
      )}
      <BillingAuditCard />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Stat icon={DollarSign} label="Revenue" value={`$${stats.revenue.toFixed(2)}`} />
        <Stat icon={TrendingUp} label="Profit" value={`$${stats.profit.toFixed(2)}`} />
        <Stat icon={PhoneCall} label="Total calls" value={stats.total_calls} />
        <Stat icon={Clock} label="Minutes" value={stats.total_minutes} />
        <Stat icon={Users} label="Users" value={stats.total_users} />
        <Stat icon={DollarSign} label="Top-ups" value={`$${stats.total_topups.toFixed(2)}`} />
        <Stat icon={TrendingUp} label="Provider cost" value={`$${stats.provider_cost.toFixed(2)}`} />
        <Stat icon={AlertTriangle} label="Failed calls" value={stats.failed_calls} accent="bg-red-100" />
        <Stat icon={PhoneCall} label="Follow-up SMS sent" value={stats.sms_sent ?? 0} />
        <Stat icon={Star} label="Avg call rating" value={stats.rated_calls ? `${stats.avg_rating} ★ (${stats.rated_calls})` : "—"} />
        <Stat icon={Star} label="Rating by mode" value={
          stats.rating_by_mode && Object.keys(stats.rating_by_mode).length
            ? Object.entries(stats.rating_by_mode)
                .map(([m, v]) => `${m === "browser" ? "Device" : m === "twilio" ? "Callback" : m} ${v.avg}★(${v.count})`)
                .join(" · ")
            : "—"
        } />
      </div>

      <div className="bg-white border border-black/10 rounded-2xl p-6 shadow-sm mb-6">
        <h3 className="font-heading font-bold mb-4">Revenue by destination</h3>
        {stats.country_breakdown.length === 0 ? (
          <p className="text-zinc-400 text-sm py-8 text-center">No completed calls yet.</p>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={stats.country_breakdown.slice(0, 8)}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
              <XAxis dataKey="country" tick={AXIS_TICK} angle={-15} textAnchor="end" height={50} />
              <YAxis tick={AXIS_TICK} />
              <Tooltip formatter={(v) => `$${Number(v).toFixed(2)}`} />
              <Bar dataKey="revenue" fill="hsl(224 64% 33%)" radius={BAR_RADIUS} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="bg-white border border-black/10 rounded-2xl shadow-sm overflow-hidden">
        <h3 className="font-heading font-bold p-5 pb-3">Recent calls</h3>
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-y border-black/10 text-xs uppercase tracking-wide text-zinc-400 font-bold text-left">
            <tr>
              <th className="px-5 py-2.5">Destination</th>
              <th className="px-5 py-2.5">Number</th>
              <th className="px-5 py-2.5">Status</th>
              <th className="px-5 py-2.5 text-right">Cost</th>
            </tr>
          </thead>
          <tbody>
            {stats.recent_calls.map((c) => (
              <tr key={c.id} className="border-b border-black/5 last:border-0">
                <td className="px-5 py-3">{c.flag} {c.country}</td>
                <td className="px-5 py-3 font-mono text-zinc-500">{c.number}</td>
                <td className="px-5 py-3 capitalize">{c.status}</td>
                <td className="px-5 py-3 text-right font-mono font-semibold">${(c.cost ?? 0).toFixed(2)}</td>
              </tr>
            ))}
            {stats.recent_calls.length === 0 && (
              <tr><td colSpan={4} className="px-5 py-8 text-center text-zinc-400">No calls yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LowBalanceThresholdCard() {
  const [value, setValue] = useState("");
  const [current, setCurrent] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings/low-balance-threshold").then(({ data }) => setCurrent(data.threshold_usd)).catch(() => {});
  }, []);

  const save = async () => {
    const n = parseFloat(value);
    if (Number.isNaN(n) || n < 0) return toast.error("Enter a valid amount in USD");
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings/low-balance-threshold", { threshold_usd: n });
      setCurrent(data.threshold_usd);
      setValue("");
      toast.success(`Low-balance warning now fires below $${data.threshold_usd.toFixed(2)}`);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-black/10 rounded-2xl p-5 shadow-sm mb-6 flex flex-wrap items-end gap-3" data-testid="low-balance-threshold-card">
      <div>
        <label className="block text-xs font-bold uppercase tracking-wide text-zinc-400 mb-1.5">Low-balance warning email (USD)</label>
        <input
          type="number" step="0.5" min="0"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={current === null ? "…" : `currently $${current.toFixed(2)}`}
          data-testid="low-balance-threshold-input"
          className="w-52 bg-zinc-50 border border-black/10 rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-primary outline-none"
        />
      </div>
      <button
        onClick={save}
        disabled={saving || !value}
        data-testid="low-balance-threshold-save"
        className="h-11 px-5 rounded-xl bg-zinc-900 text-white font-semibold hover:bg-zinc-800 transition-colors flex items-center gap-2 disabled:opacity-60"
      >
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
      </button>
      <p className="basis-full text-xs text-zinc-400 -mt-1">
        Users get the "Your balance is low" email the moment a call drops their wallet below this amount.
        {current !== null && <> Current: <b className="text-zinc-600" data-testid="low-balance-threshold-current">${current.toFixed(2)}</b></>}
      </p>
    </div>
  );
}

function RatesAdmin() {
  const [rates, setRates] = useState([]);
  const [margin, setMargin] = useState("");
  const [savingId, setSavingId] = useState(null);
  const [applying, setApplying] = useState(false);

  const load = useCallback(() => api.get("/admin/rates").then(({ data }) => setRates(data)), []);
  useEffect(() => { load(); }, [load]);

  const updateField = (id, field, value) => {
    setRates((rs) => rs.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
  };

  const save = async (r) => {
    setSavingId(r.id);
    try {
      await api.put(`/admin/rates/${r.id}`, {
        provider_cost: parseFloat(r.provider_cost),
        margin_percent: parseFloat(r.margin_percent),
      });
      toast.success(`${r.country} rate updated`);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSavingId(null);
    }
  };

  const applyGlobalMargin = async () => {
    if (!margin) return;
    setApplying(true);
    try {
      await api.post("/admin/margin", null, { params: { margin_percent: parseFloat(margin) } });
      toast.success(`Global margin set to ${margin}%`);
      setMargin("");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setApplying(false);
    }
  };

  return (
    <div data-testid="admin-rates">
      <TwilioCostSyncCard onSynced={load} />
      <LowBalanceThresholdCard />
      <div className="bg-white border border-black/10 rounded-2xl p-5 shadow-sm mb-6 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-bold uppercase tracking-wide text-zinc-400 mb-1.5">Apply global margin (%)</label>
          <input
            type="number"
            value={margin}
            onChange={(e) => setMargin(e.target.value)}
            placeholder="e.g. 35"
            data-testid="global-margin-input"
            className="w-40 bg-zinc-50 border border-black/10 rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-primary outline-none"
          />
        </div>
        <button
          onClick={applyGlobalMargin}
          disabled={applying}
          data-testid="apply-global-margin"
          className="h-11 px-5 rounded-xl bg-zinc-900 text-white font-semibold hover:bg-zinc-800 transition-colors flex items-center gap-2 disabled:opacity-60"
        >
          {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : <TrendingUp className="h-4 w-4" />} Apply to all
        </button>
      </div>

      <div className="bg-white border border-black/10 rounded-2xl shadow-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-rates-table">
          <thead className="bg-zinc-50 border-b border-black/10 text-xs uppercase tracking-wide text-zinc-400 font-bold text-left">
            <tr>
              <th className="px-5 py-3">Country</th>
              <th className="px-5 py-3">Provider cost</th>
              <th className="px-5 py-3">Margin %</th>
              <th className="px-5 py-3">Sell rate</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {rates.map((r) => (
              <tr key={r.id} className="border-b border-black/5 last:border-0" data-testid={`admin-rate-${r.iso}`}>
                <td className="px-5 py-3 font-semibold whitespace-nowrap">{r.flag} {r.country}</td>
                <td className="px-5 py-3">
                  <input
                    type="number" step="0.001" value={r.provider_cost}
                    onChange={(e) => updateField(r.id, "provider_cost", e.target.value)}
                    data-testid={`provider-cost-${r.iso}`}
                    className="w-24 bg-zinc-50 border border-black/10 rounded-lg px-2.5 py-1.5 font-mono focus:ring-2 focus:ring-primary outline-none"
                  />
                </td>
                <td className="px-5 py-3">
                  <input
                    type="number" step="1" value={r.margin_percent}
                    onChange={(e) => updateField(r.id, "margin_percent", e.target.value)}
                    data-testid={`margin-${r.iso}`}
                    className="w-20 bg-zinc-50 border border-black/10 rounded-lg px-2.5 py-1.5 font-mono focus:ring-2 focus:ring-primary outline-none"
                  />
                </td>
                <td className="px-5 py-3 font-mono font-bold text-primary">
                  ${(parseFloat(r.provider_cost) * (1 + parseFloat(r.margin_percent) / 100)).toFixed(3)}
                </td>
                <td className="px-5 py-3 text-right">
                  <button
                    onClick={() => save(r)}
                    disabled={savingId === r.id}
                    data-testid={`save-rate-${r.iso}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-white text-xs font-semibold hover:bg-[#1E40AF] transition-colors disabled:opacity-60"
                  >
                    {savingId === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function numberHealth(users) {
  if (users <= 100) return { label: "Healthy", cls: "bg-accent text-accent-foreground" };
  if (users <= 250) return { label: "Busy", cls: "bg-amber-100 text-amber-700" };
  return { label: "Overloaded", cls: "bg-red-100 text-red-700" };
}

function NumbersAdmin() {
  const [data, setData] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(() => api.get("/admin/numbers").then(({ data }) => setData(data)), []);
  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setSyncing(true);
    try {
      const { data: res } = await api.post("/admin/numbers/sync");
      toast.success(`Synced from Twilio: ${res.added} added, ${res.updated} updated`);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSyncing(false);
    }
  };

  const toggleNumber = async (n) => {
    try {
      await api.put(`/admin/numbers/${n.id}/status`, null, { params: { active: !n.active } });
      toast.success(`${n.number} ${!n.active ? "activated" : "deactivated"}`);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  if (!data) return <div className="p-8 text-center text-zinc-400">Loading…</div>;

  return (
    <div data-testid="admin-numbers">
      <div className="bg-accent border border-primary/20 rounded-2xl p-5 mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-accent-foreground max-w-xl">
          <b>Number pool.</b> Each user is stickily assigned a number here, spreading call load and
          protecting sender reputation. Buy more numbers in Twilio, then click <b>Sync</b> to import them.
        </div>
        <button
          onClick={sync}
          disabled={syncing}
          data-testid="sync-numbers-btn"
          className="h-11 px-5 rounded-xl bg-zinc-900 text-white font-semibold hover:bg-zinc-800 transition-colors flex items-center gap-2 disabled:opacity-60"
        >
          {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Sync from Twilio
        </button>
      </div>

      {data.last_synced && (
        <p className="text-xs text-zinc-400 mb-3" data-testid="last-synced">
          Last synced: {new Date(data.last_synced).toLocaleString()}
        </p>
      )}

      <div className="bg-white border border-black/10 rounded-2xl shadow-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="numbers-table">
          <thead className="bg-zinc-50 border-b border-black/10 text-xs uppercase tracking-wide text-zinc-400 font-bold text-left">
            <tr>
              <th className="px-5 py-3">Number</th>
              <th className="px-5 py-3">Label</th>
              <th className="px-5 py-3 text-right">Assigned users</th>
              <th className="px-5 py-3">Load</th>
              <th className="px-5 py-3 text-right">Total calls</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {data.numbers.map((n) => (
              <tr key={n.id} className="border-b border-black/5 last:border-0" data-testid={`number-row-${n.id}`}>
                <td className="px-5 py-3 font-mono font-semibold flex items-center gap-2">
                  <Phone className="h-4 w-4 text-primary" /> {n.number}
                </td>
                <td className="px-5 py-3 text-zinc-500">{n.friendly_name || "—"}</td>
                <td className="px-5 py-3 text-right font-mono">{n.assigned_users}</td>
                <td className="px-5 py-3">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${numberHealth(n.assigned_users).cls}`} data-testid={`number-health-${n.id}`}>
                    {numberHealth(n.assigned_users).label}
                  </span>
                </td>
                <td className="px-5 py-3 text-right font-mono">{n.total_calls}</td>
                <td className="px-5 py-3">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${n.active ? "bg-accent text-accent-foreground" : "bg-zinc-100 text-zinc-500"}`}>
                    {n.active ? "active" : "inactive"}
                  </span>
                </td>
                <td className="px-5 py-3 text-right">
                  <button
                    onClick={() => toggleNumber(n)}
                    data-testid={`toggle-number-${n.id}`}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${n.active ? "bg-red-50 text-red-700 hover:bg-red-100" : "bg-accent text-accent-foreground hover:bg-emerald-100"}`}
                  >
                    {n.active ? "Deactivate" : "Activate"}
                  </button>
                </td>
              </tr>
            ))}
            {data.numbers.length === 0 && (
              <tr><td colSpan={7} className="px-5 py-8 text-center text-zinc-400">No numbers in the pool yet. Click "Sync from Twilio".</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function UsersAdmin() {
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => api.get("/admin/users").then(({ data }) => setUsers(data)), []);
  useEffect(() => { load(); }, [load]);

  const setPw = async (u) => {
    const pw = window.prompt(`New password for ${u.email} (min 6 characters):`);
    if (pw === null) return;
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    setBusy(u.id);
    try {
      await api.put(`/admin/users/${u.id}/password`, { password: pw });
      toast.success(`Password updated for ${u.email}`);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const sendReset = async (u) => {
    if (!window.confirm(`Email a password-reset link to ${u.email}?`)) return;
    setBusy(u.id);
    try {
      const { data } = await api.post(`/admin/users/${u.id}/send-reset`);
      toast.success(data.message || `Reset link emailed to ${u.email}`);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const toggle = async (u) => {
    const next = u.status === "active" ? "suspended" : "active";
    setBusy(u.id);
    try {
      await api.put(`/admin/users/${u.id}/status`, null, { params: { status: next } });
      toast.success(`${u.email} ${next}`);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="bg-white border border-black/10 rounded-2xl shadow-sm overflow-x-auto" data-testid="admin-users">
      <table className="w-full text-sm">
        <thead className="bg-zinc-50 border-b border-black/10 text-xs uppercase tracking-wide text-zinc-400 font-bold text-left">
          <tr>
            <th className="px-5 py-3">User</th>
            <th className="px-5 py-3 text-right">Balance</th>
            <th className="px-5 py-3 text-right">Calls</th>
            <th className="px-5 py-3 text-right">Spent</th>
            <th className="px-5 py-3">Status</th>
            <th className="px-5 py-3"></th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id} className="border-b border-black/5 last:border-0" data-testid={`admin-user-${u.id}`}>
              <td className="px-5 py-3">
                <div className="font-semibold flex items-center gap-2">
                  {u.name}
                  {u.suspicious && <AlertTriangle className="h-3.5 w-3.5 text-amber-500" title="Suspicious activity" />}
                </div>
                <div className="text-zinc-400 text-xs">{u.email}</div>
              </td>
              <td className="px-5 py-3 text-right font-mono">${u.balance.toFixed(2)}</td>
              <td className="px-5 py-3 text-right font-mono">{u.total_calls}</td>
              <td className="px-5 py-3 text-right font-mono">${u.total_spent.toFixed(2)}</td>
              <td className="px-5 py-3">
                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${u.status === "active" ? "bg-accent text-accent-foreground" : "bg-red-100 text-red-700"}`}>
                  {u.status}
                </span>
              </td>
              <td className="px-5 py-3 text-right whitespace-nowrap">
                <button
                  onClick={() => setPw(u)}
                  disabled={busy === u.id}
                  data-testid={`set-password-${u.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-zinc-100 text-zinc-700 hover:bg-zinc-200 transition-colors disabled:opacity-60 mr-2"
                >
                  <KeyRound className="h-3.5 w-3.5" /> Password
                </button>
                <button
                  onClick={() => sendReset(u)}
                  disabled={busy === u.id}
                  data-testid={`send-reset-${u.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors disabled:opacity-60 mr-2"
                >
                  <Mail className="h-3.5 w-3.5" /> Reset link
                </button>
                {u.role !== "admin" && (
                  <button
                    onClick={() => toggle(u)}
                    disabled={busy === u.id}
                    data-testid={`toggle-user-${u.id}`}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors disabled:opacity-60 ${u.status === "active" ? "bg-red-50 text-red-700 hover:bg-red-100" : "bg-accent text-accent-foreground hover:bg-emerald-100"}`}
                  >
                    {renderToggleIcon(busy === u.id, u.status)}
                    {u.status === "active" ? "Suspend" : "Activate"}
                  </button>
                )}
              </td>
            </tr>
          ))}
          {users.length === 0 && (
            <tr><td colSpan={6} className="px-5 py-8 text-center text-zinc-400">No users yet.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function EsimsAdmin() {
  const [data, setData] = useState(null);
  const [markup, setMarkup] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/admin/esims").then(({ data }) => {
      setData(data);
      setMarkup(String(data.markup_percent));
    }).catch(() => toast.error("Failed to load eSIM data"));
  };
  useEffect(load, []);

  const saveMarkup = async () => {
    setSaving(true);
    try {
      await api.put("/admin/esims/markup", { markup_percent: parseFloat(markup) });
      toast.success("Markup updated");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <div className="py-10 text-center text-zinc-400">Loading…</div>;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-4">
        <Stat icon={DollarSign} label="eSIM revenue" value={`$${data.revenue.toFixed(2)}`} />
        <Stat icon={TrendingUp} label="Provider cost" value={`$${data.cost.toFixed(2)}`} />
        <Stat icon={DollarSign} label="eSIM profit" value={`$${data.profit.toFixed(2)}`} accent="bg-emerald-100" />
      </div>

      <div className="bg-white border border-black/10 rounded-2xl p-5 flex items-end gap-3 flex-wrap">
        <div>
          <label className="text-xs font-bold uppercase text-zinc-400 block mb-1">Markup % over wholesale</label>
          <input
            type="number" min="0" max="500" value={markup}
            onChange={(e) => setMarkup(e.target.value)}
            data-testid="esim-markup-input"
            className="w-32 bg-zinc-50 border border-black/10 rounded-xl px-3 py-2 font-mono focus:ring-2 focus:ring-primary outline-none"
          />
        </div>
        <button onClick={saveMarkup} disabled={saving} data-testid="esim-markup-save"
          className="px-5 py-2.5 rounded-xl bg-primary text-white text-sm font-semibold hover:opacity-90 disabled:opacity-60">
          {saving ? "Saving…" : "Save markup"}
        </button>
        <p className="text-xs text-zinc-400 basis-full">Applied to all plans instantly. Example: wholesale $10 at 35% sells for $13.50.</p>
      </div>

      <div className="bg-white border border-black/10 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-left text-xs uppercase text-zinc-400">
            <tr>
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Plan</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Cost</th>
              <th className="px-4 py-3 text-right">Sold</th>
              <th className="px-4 py-3 text-right">Profit</th>
            </tr>
          </thead>
          <tbody>
            {data.orders.map((o) => (
              <tr key={o.id} className="border-t border-black/5">
                <td className="px-4 py-3 text-zinc-500">{new Date(o.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3 font-medium">{o.name}</td>
                <td className="px-4 py-3">{o.status}</td>
                <td className="px-4 py-3 text-right font-mono">${o.wholesale_usd?.toFixed(2)}</td>
                <td className="px-4 py-3 text-right font-mono">${o.retail_usd?.toFixed(2)}</td>
                <td className="px-4 py-3 text-right font-mono text-emerald-700">
                  ${(o.retail_usd - o.wholesale_usd).toFixed(2)}
                </td>
              </tr>
            ))}
            {data.orders.length === 0 && (
              <tr><td colSpan="6" className="px-4 py-8 text-center text-zinc-400">No eSIM sales yet</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
